import { useEffect, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface DialogFocusOptions {
  /** Nodo da mettere a fuoco all'apertura; senza, il primo focusabile del pannello. */
  initialRef?: RefObject<HTMLElement | null>;
  /** Dove tornare alla chiusura; senza, l'elemento che aveva il fuoco prima. */
  returnRef?: RefObject<HTMLElement | null>;
}

/**
 * Fuoco di una finestra di dialogo: entra dentro all'apertura, il Tab resta
 * confinato nel pannello, alla chiusura torna da dove era partito.
 *
 * Il ritorno del fuoco avviene solo se il nodo di destinazione e ancora nella
 * pagina: dopo un "Aggiungi comunque" il bottone che aveva aperto l'avviso non
 * esiste piu, e insistere ruberebbe il fuoco alla casella di scrittura appena
 * comparsa.
 */
export const useDialogFocus = (
  open: boolean,
  panelRef: RefObject<HTMLElement | null>,
  options: DialogFocusOptions = {}
): void => {
  const { initialRef, returnRef } = options;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = initialRef?.current || panel?.querySelector<HTMLElement>(FOCUSABLE) || panel;
    first?.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const node = panelRef.current;
      if (!node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        node.focus({ preventScroll: true });
        return;
      }
      const head = items[0];
      const tail = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!e.shiftKey && (active === tail || !node.contains(active))) {
        e.preventDefault();
        head.focus();
      } else if (e.shiftKey && (active === head || !node.contains(active))) {
        e.preventDefault();
        tail.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);

    // Il nodo di ritorno si legge alla CHIUSURA, non adesso: nel frattempo
    // React puo averlo ricreato. L'accessorio e definito qui fuori perche la
    // pulizia non tocchi direttamente `.current` di un ref.
    const returnTarget = (): HTMLElement | null => returnRef?.current || previous;

    return () => {
      document.removeEventListener('keydown', onKey, true);
      const back = returnTarget();
      if (back && document.contains(back)) back.focus?.({ preventScroll: true });
    };
  }, [open, panelRef, initialRef, returnRef]);
};

export default useDialogFocus;
